import {
    Alert,
    Box,
    Button,
    Center,
    Group,
    Image,
    Loader,
    Modal,
    Paper,
    ScrollArea,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { IconPhoto, IconPlayerPlay, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Table } from '../../../components/Table';
import { useMedia } from '../../../data/game-report';
import { deleteMedia } from '../../../data/media';
import * as remote from '../../../data/remote-data';

type MediaTableRow = {
    mediaId: number;
    mediaName: string;
    mediaType: 'IMAGE' | 'VIDEO' | 'APP' | 'URL' | 'ANIMATION';
    mediaUrl?: string;
    createdAt?: string;
    adCount: number;
    totalSpend: number;
    currency: string;
};

const MediaThumbnail = ({ row }: { row: MediaTableRow }) => {
    const [hasError, setHasError] = useState(false);

    if (!row.mediaUrl || hasError) {
        return (
            <Center w="100%" h="100%" c="dimmed">
                {row.mediaType === 'VIDEO' ? <IconPlayerPlay size={20} /> : <IconPhoto size={20} />}
            </Center>
        );
    }

    if (row.mediaType === 'APP' || row.mediaType === 'URL') {
        return (
            <Center
                w="100%"
                h="100%"
                bg="light-dark(var(--mantine-color-blue-1), var(--mantine-color-blue-9))"
                c="light-dark(var(--mantine-color-blue-6), var(--mantine-color-blue-4))"
            >
                <IconPhoto size={20} />
            </Center>
        );
    }

    if (row.mediaType === 'VIDEO') {
        return (
            <Box pos="relative" w="100%" h="100%">
                <Box
                    component="video"
                    src={row.mediaUrl}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    muted
                    preload="metadata"
                    onError={() => setHasError(true)}
                />
                <Center pos="absolute" style={{ inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
                    <Box
                        c="dark.8"
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: '50%', padding: 4 }}
                    >
                        <IconPlayerPlay size={12} />
                    </Box>
                </Center>
            </Box>
        );
    }

    // IMAGE or ANIMATION
    return (
        <>
            <Image
                src={row.mediaUrl}
                alt={row.mediaName}
                w="100%"
                h="100%"
                fit="cover"
                onError={() => setHasError(true)}
            />
            {row.mediaType === 'ANIMATION' && (
                <Text
                    size="xs"
                    pos="absolute"
                    bg="blue.6"
                    c="white"
                    px={4}
                    style={{ bottom: 0, right: 0, borderTopLeftRadius: 4 }}
                >
                    ANIM
                </Text>
            )}
        </>
    );
};

export const MediaTable = ({ gameId }: { gameId: string }) => {
    const media = useMedia(gameId);
    const selectedIds = useRef<Set<string | number>>(new Set());
    const deletionRequest = useRef<remote.RemoteData<void, string>>(remote.NotAsked);
    const [, setCounter] = useState(0);
    const [showConfirmation, setShowConfirmation] = useState(false);

    const render = useCallback(
        (fn?: () => void) => {
            fn?.();
            setCounter((c) => c + 1);
        },
        [setCounter],
    );

    const data = useMemo<remote.RemoteData<MediaTableRow[], string>>(() => {
        return remote.map(media.data, (allMedia) => {
            return allMedia.map((media) => {
                // Calculate total spend from statistics
                const totalSpend = media.ads.reduce((sum, ad) => sum + ad.cost, 0);
                const currency = media.ads.length > 0 ? media.ads[0]?.currency || 'JPY' : 'JPY';

                return {
                    mediaId: media.id,
                    mediaName: media.name,
                    mediaType: media.type,
                    mediaUrl: media.url,
                    createdAt: media.createdDate,
                    adCount: media.ads.length,
                    totalSpend,
                    currency,
                };
            });
        });
    }, [media.data]);

    const deletionStats = useMemo(() => {
        const selectedMediaIds = Array.from(selectedIds.current).map((id) => parseInt(id.toString()));
        const selectedMediaData = remote
            .withDefault(data, [])
            .filter((media) => selectedMediaIds.includes(media.mediaId));

        const totalAds = selectedMediaData.reduce((sum, media) => sum + media.adCount, 0);

        return {
            mediaCount: selectedMediaData.length,
            adCount: totalAds,
            mediaNames: selectedMediaData.map((media) => media.mediaName),
        };
    }, [data, selectedIds.current.size]); // Using size as dependency to trigger recalculation

    const handleDelete = useCallback(async () => {
        setShowConfirmation(true);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (remote.isLoading(deletionRequest.current)) return;
        if (selectedIds.current.size === 0) return;

        setShowConfirmation(false);
        const mediaIdsToDelete = Array.from(selectedIds.current).map((id) => parseInt(id.toString()));

        try {
            render(() => {
                deletionRequest.current = remote.Loading;
            });

            const result = await deleteMedia({
                gameId,
                mediaIds: mediaIdsToDelete,
            });

            render(() => {
                if (result.errors.length > 0) {
                    const errorMessages = result.errors.map((e) => `Media ${e.mediaId}: ${e.error}`).join('\n');
                    deletionRequest.current = remote.Failure(errorMessages);
                } else {
                    deletionRequest.current = remote.Success(undefined);
                    selectedIds.current.clear();
                    void media.load();
                }
            });
        } catch (error: any) {
            render(() => {
                deletionRequest.current = remote.Failure(`Failed to delete media: ${error.message}`);
            });
        }
    }, [gameId, selectedIds, deletionRequest, media, render]);

    // Handle keyboard events for confirmation dialog
    useEffect(() => {
        if (!showConfirmation) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShowConfirmation(false);
            } else if (event.key === 'Enter') {
                void confirmDelete();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showConfirmation, confirmDelete]);

    return (
        <Stack gap="md">
            {/* Confirmation Modal */}
            <Modal opened={showConfirmation} onClose={() => setShowConfirmation(false)} title="Confirm Deletion">
                <Box mt={12} ta="center">
                    <Center
                        mx="auto"
                        h={48}
                        w={48}
                        bg="light-dark(var(--mantine-color-red-1), var(--mantine-color-red-9))"
                        c="light-dark(var(--mantine-color-red-8), var(--mantine-color-red-4))"
                        style={{ borderRadius: '50%' }}
                    >
                        <IconTrash size={24} />
                    </Center>
                    <Title order={4} mt="md">
                        Confirm Deletion
                    </Title>
                    <Box mt="xs" px="xl" py={12}>
                        <Text c="dimmed">You are about to delete:</Text>
                        <Box mt={12}>
                            <Paper
                                bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))"
                                p={12}
                                radius="sm"
                                component={Stack}
                                gap="xs"
                            >
                                <Group justify="space-between">
                                    <Text>Media files:</Text>
                                    <Text c="red" fw={700}>
                                        {deletionStats.mediaCount}
                                    </Text>
                                </Group>
                                {deletionStats.adCount > 0 && (
                                    <Group justify="space-between">
                                        <Text>Associated ads:</Text>
                                        <Text c="red" fw={700}>
                                            {deletionStats.adCount}
                                        </Text>
                                    </Group>
                                )}
                            </Paper>
                            {deletionStats.mediaNames.length > 0 && (
                                <Box mt={12}>
                                    <Text size="xs" c="dimmed" display="block" mb="xs">
                                        Media files to be deleted:
                                    </Text>
                                    <Paper
                                        bg="light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6))"
                                        radius="sm"
                                        p="xs"
                                        mah={128}
                                        component={ScrollArea}
                                    >
                                        {deletionStats.mediaNames.map((name, index) => (
                                            <Text key={index} size="xs" display="block" truncate>
                                                • {name}
                                            </Text>
                                        ))}
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                        <Text c="red" mt={12}>
                            <strong>This action cannot be undone.</strong>
                        </Text>
                    </Box>
                </Box>
                <Group justify="flex-end" mt="md">
                    <Button onClick={() => setShowConfirmation(false)} variant="subtle" fullWidth>
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} color="red" fullWidth>
                        Delete
                    </Button>
                </Group>
            </Modal>

            {remote.matchAll(deletionRequest.current, {
                NotAsked: () => null,
                Loading: () => null,
                Failure: (error) => (
                    <Alert color="red" title="Deletion Error">
                        <Text>{error}</Text>
                    </Alert>
                ),
                Success: () => (
                    <Alert color="green" title="Media Deleted">
                        <Text>Selected media has been deleted successfully.</Text>
                    </Alert>
                ),
            })}

            {remote.isNotAsked(media.data) ? (
                <Box ta="center" py="xl">
                    <Button onClick={media.load} leftSection={<IconPhoto size={16} />}>
                        Load Media
                    </Button>
                </Box>
            ) : remote.isFailure(media.data) ? (
                <Alert color="red" title="Failed to load media">
                    <Text>{media.data.value}</Text>
                </Alert>
            ) : (
                <Table
                    storageKey="media"
                    data={remote.withDefault(data, [])}
                    loading={remote.isLoading(media.data)}
                    id={(row) => row.mediaId}
                    defaultSortColumn="Media Name"
                    pagination={{
                        pageSize: 20,
                        showPagination: true,
                    }}
                    selection={{
                        selectedIds: selectedIds.current,
                        onSelectionChange: (newSelectedIds) => {
                            render(() => {
                                selectedIds.current = newSelectedIds;
                            });
                        },
                        actions: [
                            {
                                label: remote.isLoading(deletionRequest.current) ? 'Deleting...' : 'Delete Selected',
                                onClick: handleDelete,
                                disabled: remote.isLoading(deletionRequest.current),
                                variant: 'filled',
                                color: 'red',
                                startIcon: remote.isLoading(deletionRequest.current) ? (
                                    <Loader size={16} color="white" />
                                ) : (
                                    <IconTrash size={16} />
                                ),
                            },
                        ],
                    }}
                    density="xs"
                    columns={[
                        {
                            name: 'Preview',
                            alt: 'Media preview',
                            width: 56,
                            type: 'thumbnail' as const,
                            render: (row) => (
                                <Group gap={0}>
                                    <Paper
                                        withBorder
                                        radius="sm"
                                        pos="relative"
                                        w={40}
                                        h={40}
                                        bg="light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))"
                                        style={{ overflow: 'hidden' }}
                                    >
                                        <MediaThumbnail row={row} />
                                    </Paper>
                                </Group>
                            ),
                        },
                        {
                            name: 'Media Name',
                            alt: 'Name of the media file',
                            render: (row) => row.mediaName,
                            sort: sort.mediaName,
                        },
                        {
                            name: 'Upload Date',
                            alt: 'Date when the media was uploaded',
                            type: 'date' as const,
                            width: 150,
                            render: (row) => (row.createdAt ? dayjs(row.createdAt).format('MMM D, YYYY') : 'No date'),
                            sort: sort.createdAt,
                            defaultDirection: 'desc' as const,
                        },
                        {
                            name: 'Ads',
                            alt: 'Number of active and inactive ads using this media',
                            type: 'number' as const,
                            width: 80,
                            render: (row) => (
                                <Text c={row.adCount > 0 ? 'green' : 'dimmed'} inherit>
                                    <strong>{format.number(row.adCount)}</strong>
                                </Text>
                            ),
                            sort: sort.adCount,
                            defaultDirection: 'desc' as const,
                        },
                        {
                            name: 'Total Spend (30d)',
                            alt: 'Total ad spend for the past 30 days',
                            type: 'currency' as const,
                            width: 150,
                            render: (row) => (
                                <Text inherit>
                                    <strong>{format.currency(row.totalSpend, row.currency)}</strong>
                                </Text>
                            ),
                            sort: sort.totalSpend,
                            defaultDirection: 'desc' as const,
                        },
                    ]}
                />
            )}
        </Stack>
    );
};

// Format utilities
const format = {
    currency: (amount: number, currency: string): string =>
        new Intl.NumberFormat('en-JP', {
            style: 'currency',
            currency,
        }).format(amount),
    number: (amount: number): string =>
        new Intl.NumberFormat('en-JP', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount),
};

// Sort utilities
const sort = {
    mediaName: (a: MediaTableRow, b: MediaTableRow) => a.mediaName.localeCompare(b.mediaName),
    adCount: (a: MediaTableRow, b: MediaTableRow) => a.adCount - b.adCount,
    totalSpend: (a: MediaTableRow, b: MediaTableRow) => a.totalSpend - b.totalSpend,
    createdAt: (a: MediaTableRow, b: MediaTableRow) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    },
};
